import { commitSetAccount } from './../admin/mutations';
import { IAccount, IIngredientCreate, IMenuUpdate } from '@/interfaces';
import { api } from '@/api';
import router from '@/router';
import { getLocalToken, removeLocalToken, saveLocalToken } from '@/utils';
import { AxiosError } from 'axios';
import { getStoreAccessors } from 'typesafe-vuex';
import { ActionContext } from 'vuex';
import { commitSetMenu } from '../menu/mutations';
import { State } from '../state';
import {
    commitAddNotification,
    commitRemoveNotification,
    commitSetLoggedIn,
    commitSetLogInError,
    commitSetToken,
    commitSetUserProfile,
    commitSetUserPhoto,
    commitSetEmployeeProfile,
    commitSetOwnerProfile,
    commitSetSelectedAccount,
    commitSetSelectedBranch,
    commitUpdateUploadedMediaList,
    commitUpdateUploadedMediaList2,
    commitSetCurrentAccount,
    commitSetIngredients,
    commitSetIngredient,
    commitSetDefaultCurrency,
    commitSetDefaultLanguage,
    commitSetErrorLoginCause,
    commitSetAutoCompleteIngredients,
    commitSetCurrentSponsor,
commitSetAccountMenu
} from './mutations';
import { AppNotification, MainState } from './state';
import { filter } from 'vue/types/umd';


type MainContext = ActionContext<MainState, State>;

export const actions = {
    async actionLogIn(context: MainContext, payload: { username: string; password: string }) {
        try {
            const response = await api.logInGetToken(payload.username, payload.password);
            const token = response.data.access_token;
            if (token) {
                saveLocalToken(token);
                commitSetToken(context, token);
                commitSetLoggedIn(context, true);
                commitSetLogInError(context, false);
                await dispatchGetUserProfile(context);
                
                if (context.state.userProfile) {
                    if (context.state.userProfile.is_owner) {
                        await dispatchSetOwnerProfile(context);
                    } else if (context.state.userProfile.is_employee) {
                        await dispatchSetEmployeeProfile(context);
                    }
                }
                commitAddNotification(context, { content: 'Logged in', color: 'success' });
                await dispatchRouteLoggedIn(context);
            } else {
                await dispatchLogOut(context);
            }
        } catch (err) {
            commitSetLogInError(context, true);
            console.log(err);
            commitSetErrorLoginCause(context, err);
            await dispatchLogOut(context);
        }
    },
    async actionGetUserProfile(context: MainContext) {
        try {
            const response = await api.getMe(context.state.token);
            if (response.data) {
                commitSetUserProfile(context, response.data);
                if (context.state.userProfile) {
                    if (context.state.userProfile.is_owner) {
                        await dispatchSetOwnerProfile(context);
                    } else if (context.state.userProfile.is_employee) {
                        await dispatchSetEmployeeProfile(context);
                    }
                }
            }
        } catch (error) {
            await dispatchCheckApiError(context, error);
        }
    },
    async actionUpdateUserProfile(context: MainContext, payload) {
        try {
            const loadingNotification = { content: 'saving', showProgress: true };
            commitAddNotification(context, loadingNotification);
            const response = (await Promise.all([
                api.updateMe(context.state.token, payload),
                await new Promise<void>((resolve, reject) => setTimeout(() => resolve(), 500)),
            ]))[0];
            commitSetUserProfile(context, response.data);
            commitRemoveNotification(context, loadingNotification);
            commitAddNotification(context, { content: 'Profile successfully updated', color: 'success' });
        } catch (error) {
            await dispatchCheckApiError(context, error);
        }
    },
    async actionCheckLoggedIn(context: MainContext) {
        if (!context.state.isLoggedIn) {
            let token = context.state.token;
            if (!token) {
                const localToken = getLocalToken();
                if (localToken) {
                    commitSetToken(context, localToken);
                    token = localToken;
                }
            }
            if (token) {
                try {
                    const response = await api.getMe(token);
                    commitSetLoggedIn(context, true);
                    commitSetUserProfile(context, response.data);
                    if (context.state.userProfile) {
                        if (context.state.userProfile.is_owner) {
                            await dispatchSetOwnerProfile(context);
                        } else if (context.state.userProfile.is_employee) {
                            await dispatchSetEmployeeProfile(context);
                        }
                    }
                } catch (error) {
                    await dispatchRemoveLogIn(context);
                }
            } else {
                await dispatchRemoveLogIn(context);
            }
        }
    },
    async actionRemoveLogIn(context: MainContext) {
        removeLocalToken();
        commitSetToken(context, '');
        commitSetLoggedIn(context, false);
    },
    async actionLogOut(context: MainContext) {
        await dispatchRemoveLogIn(context);
        await dispatchRouteLogOut(context);
    },
    async actionUserLogOut(context: MainContext) {
        await dispatchLogOut(context);
        commitAddNotification(context, { content: 'Logged out', color: 'success' });
    },
    actionRouteLogOut(context: MainContext) {
        if (router.currentRoute.path !== '/login') {
            router.push('/login');
        }
    },
    async actionCheckApiError(context: MainContext, payload: AxiosError | any) {
        if (payload.response!.status === 401) {
            await dispatchLogOut(context);
        } else {
            if (payload.response!.data) {
                commitAddNotification(context, { content: payload.response!.data.detail, color: 'error' });
            } else {
                commitAddNotification(context, { content: 'Error al ejecutar la operación', color: 'error' });
            }
            
        }
    },
    async actionRouteLoggedIn(context: MainContext) {
        try{
            if (router.currentRoute.path === '/login' || router.currentRoute.path === '/') {
                //router.push('/main');
                const navigationResult = await router.push({name: 'main'});
                if(navigationResult){
                    console.log('Failed to navigate');
                }
            }
        } catch(err){
            console.log(err);
        }

    },
    async removeNotification(context: MainContext, payload: { notification: AppNotification, timeout: number }) {
        return new Promise((resolve, reject) => {
            setTimeout(() => {
                commitRemoveNotification(context, payload.notification);
                resolve(true);
            }, payload.timeout);
        });
    },
    async passwordRecovery(context: MainContext, payload: { username: string }) {
        const loadingNotification = { content: 'Sending password recovery email', showProgress: true };
        try {
            commitAddNotification(context, loadingNotification);
            const response = (await Promise.all([
                api.passwordRecovery(payload.username),
                await new Promise<void>((resolve, reject) => setTimeout(() => resolve(), 500)),
            ]))[0];
            commitRemoveNotification(context, loadingNotification);
            commitAddNotification(context, { content: 'Password recovery email sent', color: 'success' });
            await dispatchLogOut(context);
        } catch (error) {
            commitRemoveNotification(context, loadingNotification);
            commitAddNotification(context, { color: 'error', content: 'Incorrect username' });
        }
    },
    async resetPassword(context: MainContext, payload: { password: string, token: string }) {
        const loadingNotification = { content: 'Resetting password', showProgress: true };
        try {
            commitAddNotification(context, loadingNotification);
            const response = (await Promise.all([
                api.resetPassword(payload.password, payload.token),
                await new Promise<void>((resolve, reject) => setTimeout(() => resolve(), 500)),
            ]))[0];
            commitRemoveNotification(context, loadingNotification);
            commitAddNotification(context, { content: 'Password successfully reset', color: 'success' });
            await dispatchLogOut(context);
        } catch (error) {
            commitRemoveNotification(context, loadingNotification);
            commitAddNotification(context, { color: 'error', content: 'Error resetting password' });
        }
    },
    async getUserPhoto(context: MainContext, id: number) {
        try {
            /*const response = await api.getPhoto(context.state.token, id);
            if (response) {
                
                const newBlob = new Blob( [ response.data ], { type: 'image/png' } );
                //const data = Buffer.from(response.data);
              // commitSetUserPhoto(context, newBlob);
            }*/
        } catch (error) {
            await dispatchCheckApiError(context, error);
        }
    },
    async getEmployeeProfile(context: MainContext) {
        try {
            if (context.state.userProfile) {
                const response = await api.getEmployee(context.state.token, 
                    context.state.userProfile.id);
                    if (response) {
                        commitSetEmployeeProfile(context, response.data);
                        commitSetSelectedAccount(context, response.data.account_id);
                        commitSetSelectedBranch(context, response.data.branch_id);
                        const account = response.data.account;
                        commitSetDefaultCurrency(context, account.default_currency);
                        commitSetDefaultLanguage(context, account.main_language);
                    }
            }
        } catch (error) {
            await dispatchCheckApiError(context, error);
        }
    },
    async getOwnerProfile(context: MainContext) {
        try {
            const response = await api.getOwnerProfile(context.state.token);
            if (response) {
                commitSetOwnerProfile(context, response.data);
                commitSetSelectedAccount(context, response.data.accounts_id[0]);
                const account: IAccount = response.data.accounts[0];
                commitSetCurrentAccount(context, account)
                commitSetDefaultCurrency(context, account.default_currency);
                commitSetDefaultLanguage(context, account.main_language);

                commitSetSelectedBranch(context, account.branches[0].id);
                
            }
        } catch (error) {
            await dispatchCheckApiError(context, error);
        }
    },
    async uploadUserPicture(context: MainContext, payload: FormData) {
        try {   
            const response = await api.uploadProfilePicture(context.state.token,
                payload);
            if (response) {
                commitUpdateUploadedMediaList(context, response.data);
            }
        } catch(error) {
            await dispatchCheckApiError(context, error);
        }
    },
    async uploadMenuItemImages(context: MainContext, payload: FormData){
        try {
            const upto = context.state.currentAccount?.account_dir;
            const response = await api.uploadMenuImages(context.state.token,
                upto,
                payload);
            if (response) {
                commitUpdateUploadedMediaList2(context, response.data)
            }
        } catch(error){
            await dispatchCheckApiError(context, error);
        }
    },
    async uploadSponsorLogo(context: MainContext, payload: FormData){
        try {
            const upto = 'sponsors';
            const response = await api.uploadMenuImages(context.state.token,
                upto,
                payload);
            if (response) {
                commitUpdateUploadedMediaList2(context, response.data)
            }
        } catch(error){
            await dispatchCheckApiError(context, error);
        }
    },
    async addIngredient(context: MainContext, payload: IIngredientCreate) {
        try {
            const response = await api.addIngredient(context.state.token, payload);
            if (response) {
                commitAddNotification(context, { content: 'Ingrediente registrado', color: 'success' });
            }
        } catch(error) {
            await dispatchCheckApiError(context, error);
        }
    },
    async getIngredients(context: MainContext, payload: {limit: number, filter: string | null, lang: string}) {
        try {
            const response =  await api.getIngredients(context.state.token, payload.limit, payload.filter, payload.lang);
            if(response) {
                commitSetIngredients(context, response.data);
            }
        } catch(error) {
            console.log(error);
        }
    },
    async getAutoCompleteIngredients(context: MainContext, payload: {limit: number, filter: string | null, lang: string}) {
        try {
            const response =  await api.getIngredients(context.state.token, payload.limit, payload.filter, payload.lang);
            if(response) {
                commitSetAutoCompleteIngredients(context, response.data);
            }
        } catch(error) {
            console.log(error);
        }
    },
    async getIngredient(context: MainContext, id: number) {
        try {
            const response =  await api.getIngredient(context.state.token, id);
            if(response) {
                commitSetIngredient(context, response.data);
            }
        } catch(error) {
            console.log(error);
        }
    },
    //get sponsor (if any) for current account
    async getCurrentSponsor(context: MainContext) {
        try {
            if(!context.state.currentAccount) return;
            if(!context.state.currentAccount.sponsor_id) return;
            const response =  await api.getSponsor(context.state.token, context.state.currentAccount.sponsor_id);
            if(response) {
                commitSetCurrentSponsor(context, response.data);
            }
        } catch(error) {
            console.log(error);
        }
    },
    async updateMenu(context: MainContext, payload: {id: number, data: IMenuUpdate}) {
        try {
            const response = await api.updateMenu(context.state.token, payload.id, payload.data);
            if (response) {
                //commitSetMenu(context, response.data);
                commitSetAccountMenu(context, response.data);
                commitAddNotification(context, { content: 'Menu actualizado', color: 'success' });
            }
        } catch(error) {
            await dispatchCheckApiError(context, error);
        }
    },
    async generateQRCodes(context: MainContext, payload:{branch_id: number, qr_code_style:string}) {
        try {
            const response = await api.generateTablesQRCodes(context.state.token, payload.branch_id, payload.qr_code_style);
            if(response){
                commitAddNotification(context, {content: 'Generando los códigos qr', color:'success'});
            }
        } catch(error) {
            console.log(error);
        }
        
    }
};

const { dispatch } = getStoreAccessors<MainState | any, State>('');

export const dispatchCheckApiError = dispatch(actions.actionCheckApiError);
export const dispatchCheckLoggedIn = dispatch(actions.actionCheckLoggedIn);
export const dispatchGetUserProfile = dispatch(actions.actionGetUserProfile);
export const dispatchLogIn = dispatch(actions.actionLogIn);
export const dispatchLogOut = dispatch(actions.actionLogOut);
export const dispatchUserLogOut = dispatch(actions.actionUserLogOut);
export const dispatchRemoveLogIn = dispatch(actions.actionRemoveLogIn);
export const dispatchRouteLoggedIn = dispatch(actions.actionRouteLoggedIn);
export const dispatchRouteLogOut = dispatch(actions.actionRouteLogOut);
export const dispatchUpdateUserProfile = dispatch(actions.actionUpdateUserProfile);
export const dispatchRemoveNotification = dispatch(actions.removeNotification);
export const dispatchPasswordRecovery = dispatch(actions.passwordRecovery);
export const dispatchResetPassword = dispatch(actions.resetPassword);
export const dispatchSetUserPhoto = dispatch(actions.getUserPhoto);
export const dispatchSetOwnerProfile = dispatch(actions.getOwnerProfile);
export const dispatchSetEmployeeProfile = dispatch(actions.getEmployeeProfile);
export const dispatchUploadUserPicture = dispatch(actions.uploadUserPicture);
export const dispatchUploadMenuItemImages = dispatch(actions.uploadMenuItemImages);
export const dispatchGetIngredients = dispatch(actions.getIngredients);
export const dispatchAddIngredient = dispatch(actions.addIngredient);
export const dispatchGetIngredient = dispatch(actions.getIngredient);
export const dispatchGetAutoCompleteIngredients = dispatch(actions.getAutoCompleteIngredients);
export const dispatchUploadSponsorLogo = dispatch(actions.uploadSponsorLogo);
export const dispatchGetCurrentSponsor = dispatch(actions.getCurrentSponsor);
export const dispatchUpdateMenu = dispatch(actions.updateMenu);
export const dispatchGenerateQRCodes = dispatch(actions.generateQRCodes);